import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { 
  Building2, 
  Users, 
  Calendar, 
  MessageCircle, 
  Shield,
  Flag,
  Clock,
  FolderArchive,
  AlertCircle,
  FileX,
  ArrowRight,
  Phone,
  Mail,
  ChevronDown
} from 'lucide-react';
import { useState } from 'react';

// ============== HEADER ==============
function LandingHeader() {
  return (
    <header className="sticky top-0 z-50 bg-base-white/80 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <img 
              src="/images/Condo-logo-dark.png" 
              alt="Condo logo" 
              className="h-8"
            />
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-sm font-medium text-gray-700 hover:text-condo-dark transition-colors">
              Funksjoner
            </a>
            <a href="#benefits" className="text-sm font-medium text-gray-700 hover:text-condo-dark transition-colors">
              Fordeler
            </a>
            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-condo-dark transition-colors">
              Kontakt
            </a>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="outline" size="sm">
                Logg inn
              </Button>
            </Link>
            <Link to="/signup" className="hidden sm:block">
              <Button variant="primary" size="sm">
                Kom i gang
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

// ============== HERO ==============
function HeroSection() {
  return (
    <section className="relative py-6 sm:py-8 lg:py-10 px-4 sm:px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-light-2 via-light-1 to-white opacity-60" />
      
      <div className="max-w-5xl mx-auto relative">
        <div className="text-center space-y-4 sm:space-y-5">
          {/* Main Heading */}
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-condo-dark leading-tight tracking-tight">
              Én plattform for styret og beboerne.
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Meldinger, reservasjoner og dokumenter på ett sted – med en digital assistent som fikser rutineoppgavene.
            </p>
          </div>
          
          {/* App Preview */}
          <div className="flex justify-center">
            <div className="relative max-w-xl lg:max-w-2xl mx-auto">
              <div className="bg-gray-200 p-1.5 sm:p-2 rounded-lg shadow-lg">
                <img 
                  src="/images/condo-landing.jpg" 
                  alt="Condo app interface preview" 
                  className="rounded-md w-full h-auto" 
                />
              </div>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a href="#contact">
              <Button variant="primary" size="md" className="group">
                Prøv gratis i én måned
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="#contact">
              <Button variant="outline" size="md">
                Book en demo
              </Button>
            </a>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 items-center">
            <div className="flex items-center space-x-1.5">
              <Flag className="w-4 h-4 text-condo-dark" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">Bygget i Norge</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Shield className="w-4 h-4 text-condo-dark" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">Sikker og trygg</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Building2 className="w-4 h-4 text-condo-dark" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">For alle borettslag og sameier</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============== BENEFITS ==============
function BenefitsSection() {
  const benefits = [
    {
      icon: MessageCircle,
      title: "All kommunikasjon på ett sted",
      description: "Push-varsler, meldinger og chat holder beboerne og styret oppdatert."
    },
    {
      icon: Calendar,
      title: "Full kontroll på fellesressurser",
      description: "Beboerne booker vaskerom, gjesteparkering og hagebord på sekunder."
    },
    {
      icon: Clock,
      title: "Spar tid på rutineoppgaver",
      description: "Automatisk håndtering av reserveringer, påminnelser og repeterende spørsmål."
    },
    {
      icon: FolderArchive,
      title: "Trygt dokumentarkiv",
      description: "Vedtekter, protokoller og kontrakter lagres kryptert – alltid siste versjon, søkbar på sekunder."
    }
  ];

  return (
    <section id="benefits" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-light-2/50 to-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center space-y-4 mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-condo-dark leading-tight">
            Hvorfor velge Condo?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Se hvordan våre løsninger transformerer måten borettslag drives på.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {benefits.map((benefit, index) => (
            <Card key={index} padding="lg" className="bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-condo-dark/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-condo-dark" />
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== CHALLENGES ==============
function ChallengesSection() {
  const challenges = [
    {
      icon: MessageCircle,
      title: "Gjentagende spørsmål",
      description: "Er Airbnb lov? Hvordan får jeg ny nøkkel? Når tømmes søppelcontaineren?"
    },
    {
      icon: AlertCircle,
      title: "Fra flaks til forutsigbarhet",
      description: "Gjesteparkering burde kunne planlegges, ikke gambles på."
    },
    {
      icon: Users,
      title: "Naboer som ikke møtes",
      description: "Bare én av fem kjenner naboens navn. Condo gjør det enklere å bygge fellesskap."
    },
    {
      icon: FileX,
      title: "Dokumenter på avveie",
      description: "Vedtekter og referater drukner i e-posttråder – viktige beslutninger blir borte."
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-condo-orange/10 to-condo-yellow/20">
      <div className="max-w-5xl mx-auto">
        <div className="text-center space-y-4 mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-condo-dark leading-tight">
            Kjenner du deg igjen?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Mange styreledere sliter med de samme utfordringene. Du er ikke alene.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {challenges.map((challenge, index) => (
            <Card key={index} padding="md" className="bg-white border-orange-8/30 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-condo-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <challenge.icon className="w-5 h-5 text-orange-10" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">{challenge.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{challenge.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== FEATURES ==============
function FeaturesSection() {
  return (
    <section id="features" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto text-center">
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-condo-dark leading-tight">
            Alt samlet. Mindre mas.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Slutt med kaos og misforståelser. Condo samler alt på én plass og automatiserer 
            det meste, så du kan fokusere på det som virkelig betyr noe.
          </p>
        </div>
        
        <Link to="/signup">
          <Button variant="primary" size="lg" className="group">
            Se alle funksjoner
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

// ============== FAQ ==============
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const faqs = [
    {
      question: "Kan vi prøve Condo gratis før vi bestemmer oss?",
      answer: "Ja. Vi tilbyr en kort prøveperiode der dere kan teste alle kjernefunksjonene uten binding."
    },
    {
      question: "Hva gjør AI-assistenten?",
      answer: "Den foreslår ferdige svar på innkommende henvendelser – eller svarer helt selv hvis styret ønsker det. Alt hentes fra borettslagets egne vedtekter, regler og dokumenter, så dere slipper å svare på de samme spørsmålene om og om igjen."
    },
    {
      question: "Hvordan kommer vi i gang?",
      answer: "Vi importerer eksisterende dokumenter, setter opp brukere og viser dere rundt. Det tar som regel 1–2 uker fra «ja» til alle har tilgang."
    },
    {
      question: "Er dataene våre sikre?",
      answer: "All informasjon krypteres og lagres i norske datasentre. Vipps / BankID-innlogging er også rett rundt hjørnet for ekstra trygg verifisering."
    },
    {
      question: "Hva koster Condo?",
      answer: "Kontakt oss, så får dere et tilbud som passer akkurat deres borettslag eller sameie."
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-light-1">
      <div className="max-w-3xl mx-auto">
        <div className="text-center space-y-4 mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-condo-dark leading-tight">
            Ofte stilte spørsmål
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Her finner du svar på de vanligste spørsmålene om Condo
          </p>
        </div>
        
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="border border-gray-200 rounded-xl bg-white overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base sm:text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-condo-dark flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              {openIndex === index && (
                <div className="px-4 sm:px-6 pb-4">
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== CTA ==============
function CTASection() {
  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-gradient-to-r from-condo-dark to-base-dark1 rounded-2xl p-6 sm:p-8 md:p-12 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10 rounded-2xl" />
          <div className="relative space-y-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              Vil du gjøre styrehverdagen enklere?
            </h2>
            <p className="text-base sm:text-lg md:text-xl opacity-90 leading-relaxed max-w-2xl mx-auto">
              Book en gratis demo og se hvordan Condo kan forenkle hverdagen for både styret og beboere.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/signup">
                <Button 
                  variant="secondary" 
                  size="lg" 
                  className="bg-white text-condo-dark hover:bg-condo-light border-0 group"
                >
                  Book en demo nå
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            
            <div className="pt-8 border-t border-white/20">
              <p className="text-sm opacity-75 mb-4">Eller ta kontakt direkte</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="tel:+4746839838" 
                  className="flex items-center space-x-2 text-sm hover:opacity-80 transition-opacity"
                >
                  <Phone className="w-4 h-4" />
                  <span>+47 46 83 98 38</span>
                </a>
                <a 
                  href="mailto:kontakt@mincondo.no" 
                  className="flex items-center space-x-2 text-sm hover:opacity-80 transition-opacity"
                >
                  <Mail className="w-4 h-4" />
                  <span>kontakt@mincondo.no</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============== FOOTER ==============
function LandingFooter() {
  return (
    <footer className="py-10 sm:py-12 px-4 sm:px-6 bg-condo-dark text-white">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/images/Condo-logo-light.png" 
                alt="Condo logo" 
                className="h-8"
              />
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              Din digitale bakgård for effektiv styring av borettslag og sameier.
            </p>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-semibold">Produktet</h4>
            <div className="space-y-2">
              <a href="#features" className="block text-sm text-white/80 hover:text-white transition-colors">Funksjoner</a>
              <a href="#benefits" className="block text-sm text-white/80 hover:text-white transition-colors">Fordeler</a>
              <a href="#contact" className="block text-sm text-white/80 hover:text-white transition-colors">Priser</a>
              <a href="#contact" className="block text-sm text-white/80 hover:text-white transition-colors">Sikkerhet</a>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-semibold">Ressurser</h4>
            <div className="space-y-2">
              <Link to="/blogg" className="block text-sm text-white/80 hover:text-white transition-colors">Blogg</Link>
              <a href="#contact" className="block text-sm text-white/80 hover:text-white transition-colors">Hjelp</a>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-semibold">Juridisk</h4>
            <div className="space-y-2">
              <a href="#contact" className="block text-sm text-white/80 hover:text-white transition-colors">Kontakt oss</a>
              <a href="mailto:admin@mincondo.no" className="block text-sm text-white/80 hover:text-white transition-colors">Support</a>
              <Link to="/personvern" className="block text-sm text-white/80 hover:text-white transition-colors">Personvern</Link>
              <Link to="/vilkar" className="block text-sm text-white/80 hover:text-white transition-colors">Vilkår</Link>
            </div>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/20 text-center">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} Condo, en tjeneste fra Twain AS. Alle rettigheter reservert.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ============== MAIN LANDING PAGE ==============
export function Landing() {
  return (
    <>
      <Helmet>
        <title>Condo - Én plattform for styret og beboerne</title>
        <meta name="description" content="Condo samler kommunikasjon, dokumenter og henvendelser for borettslag og sameier. Styret slipper mas. Beboerne får svar." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mincondo.no/landing" />
        <meta property="og:title" content="Condo - Én plattform for styret og beboerne" />
        <meta property="og:description" content="Condo samler kommunikasjon, dokumenter og henvendelser for borettslag og sameier. Styret slipper mas. Beboerne får svar." />
        <meta property="og:image" content="https://mincondo.no/images/condo-landing.jpg" />
        <meta property="og:site_name" content="Condo" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://mincondo.no/landing" />
        <meta name="twitter:title" content="Condo - Én plattform for styret og beboerne" />
        <meta name="twitter:description" content="Condo samler kommunikasjon, dokumenter og henvendelser for borettslag og sameier." />
        <meta name="twitter:image" content="https://mincondo.no/images/condo-landing.jpg" />
        
        {/* Canonical */}
        <link rel="canonical" href="https://mincondo.no/landing" />
      </Helmet>
      
      <div className="min-h-screen bg-base-white">
        <LandingHeader />
        <HeroSection />
        <BenefitsSection />
        <ChallengesSection />
        <FeaturesSection />
        <FAQSection />
        <CTASection />
        <LandingFooter />
      </div>
    </>
  );
}
